import type { EmailAttachmentControlProps } from '@/components/affiliate/bulk-email/EmailAttachmentsField';
import { EmailInvitationMessageEditor } from '@/components/editor/lexical/email-invitation-message-editor';
import { Alert } from '@/components/ui/alert';
import { validateRequiredVariables } from '@/libs/utils/lexical-utils';
import { AlertDescription, Text } from '@chakra-ui/react';
import type { AvailableVariable } from '@fluenceur/ui';
import type { EditorState } from 'lexical';
import { useCallback, useMemo, useState, type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageLanguageToggle } from '../components';
import InvitationMessageCardSkeleton from '../components/invitation-message-card-skeleton';
import { useOutreachLanguage } from './use-outreach-language';

/**
 * What the composer reports on every editor change / reset. It NEVER sends. The
 * composer surfaces its own missing-variable Alert internally, so callers only
 * need the body + validity to drive their subject + send.
 */
export interface OutreachEmailState {
  /** Compiled HTML body (variable chips serialized to `{{key}}`); '' when empty. */
  bodyHtml: string;
  /**
   * Latest Lexical editor state as JSON; null after a reset. Consumed by the
   * template-library flow (untouched detection + save-as-template seed).
   */
  messageSnapshot: string | null;
  /** False when a required variable (the link chip) is missing from the body. */
  isValid: boolean;
}

interface OutreachEmailComposerProps {
  /** Resolved template content + insert-variable set for the current language. */
  templateSource: {
    initialContent: string | null;
    availableVariables: AvailableVariable[];
    isLoading: boolean;
  };
  /**
   * The mustache link the body must carry: `{{collab_link}}` (sponsored) or
   * `{{invitation_link}}` (affiliation). Enforced as required even if the
   * injected variable set forgot to flag it — idempotent when it already is.
   */
  requiredLinkVariable: string;
  /** Reports the editor state upward; the caller owns subject + send. */
  onChange: (state: OutreachEmailState) => void;
  /**
   * Trailing content of the editor footer (across from the insert-variable
   * button) — the template selector + save action, injected by the caller.
   */
  editorFooter?: ReactNode;
  attachmentControl?: EmailAttachmentControlProps;
}

/** `{{collab_link}}` → `collab_link`. */
const bareVariableKey = (variable: string): string => variable.replace(/[{}]/g, '').trim();

/**
 * Shared email compose surface for the outreach step of both collab types
 *. Wraps the Lexical email editor, the email-language toggle
 * and the required-variable guard, and reports its state up via `onChange`. It
 * owns no send logic, no subject, and no invitation/collab identifier — the
 * caller injects the template + link variable and owns the send. A missing
 * template renders an empty editor, never a broken half.
 */
export const OutreachEmailComposer: FC<OutreachEmailComposerProps> = ({
  templateSource,
  requiredLinkVariable,
  onChange,
  editorFooter,
  attachmentControl,
}) => {
  const { t } = useTranslation();
  const { selectedLanguage, languageToggleEnabled, brandDefaultLanguage, setMessageLanguage } =
    useOutreachLanguage();

  const { initialContent, availableVariables, isLoading } = templateSource;

  // Guarantee the link chip is validated even when the injected set forgot to
  // flag it required. Idempotent for both callers (their sources already mark it
  // required), so validation behaviour is unchanged.
  const linkKey = bareVariableKey(requiredLinkVariable);
  const variables = useMemo<AvailableVariable[]>(
    () =>
      availableVariables.map((variable) =>
        variable.key === linkKey && !variable.required ? { ...variable, required: true } : variable
      ),
    [availableVariables, linkKey]
  );

  const [missingVariable, setMissingVariable] = useState<string | null>(null);

  const handleEditorChange = useCallback(
    (editorState: EditorState, _plainText: string, html?: string) => {
      const snapshot = JSON.stringify(editorState.toJSON());
      // A failed preparation can return no variable definitions. The caller's
      // required link still applies, independently of that response.
      const validationVariables = variables.some((variable) => variable.key === linkKey)
        ? variables
        : [...variables, { key: linkKey, label: linkKey, required: true }];
      const validation = validateRequiredVariables(snapshot, validationVariables);
      setMissingVariable(validation.isValid ? null : (validation.missingVariables[0] ?? null));
      onChange({
        bodyHtml: typeof html === 'string' ? html : '',
        messageSnapshot: snapshot,
        isValid: validation.isValid,
      });
    },
    [variables, linkKey, onChange]
  );

  const handleReset = useCallback(() => {
    setMissingVariable(null);
    onChange({ bodyHtml: '', messageSnapshot: null, isValid: true });
  }, [onChange]);

  const handleLanguageToggle = useCallback(
    (checked: boolean) => setMessageLanguage(checked ? brandDefaultLanguage : undefined),
    [setMessageLanguage, brandDefaultLanguage]
  );

  return (
    <>
      {isLoading ? (
        <InvitationMessageCardSkeleton />
      ) : (
        <EmailInvitationMessageEditor
          // Remount to re-seed the body when the email language changes.
          key={selectedLanguage}
          initialContent={initialContent}
          availableVariables={variables}
          includeHtml
          onContentChange={handleEditorChange}
          onReset={handleReset}
          attachmentControl={attachmentControl}
          footerTrailing={editorFooter}
        />
      )}

      {missingVariable && (
        <Alert status="error" colorPalette="red" p={4} rounded="lg">
          <AlertDescription>
            <Text>
              {t('invitations.editor.missingRequiredVariable', {
                variable: `{{${missingVariable}}}`,
              })}
            </Text>
          </AlertDescription>
        </Alert>
      )}

      <MessageLanguageToggle
        enabled={languageToggleEnabled}
        value={selectedLanguage}
        onToggle={handleLanguageToggle}
        onChange={setMessageLanguage}
        disabled={isLoading}
      />
    </>
  );
};
